import React, { useRef } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { Display, Box } from '@gousto-internal/citrus-react'
import { Form, FormikProvider, setNestedObjectValues } from 'formik'
import { useSelector } from 'react-redux'

import type { PaymentProps } from 'routes/Checkout/Steps/Payment/Payment'
import { getDeliveryAddress, getEmail } from 'routes/Checkout/checkoutSelectors'

import { ErrorMessage } from '../../ErrorMessage/ErrorMessage'
import { SubmitButton } from '../../SubmitButton'
import { CheckoutBillingAddress } from '../CheckoutBillingAddress'
import { CheckoutFrame } from '../CheckoutFrame/CheckoutFrame'
import { useAddressDetailsForm } from '../useAddressDetailsForm'
import { useHostedFields } from './useHostedFields'

type CheckoutPaymentDetailsProps = Pick<
  PaymentProps,
  'submitOrder' | 'onLoginClick' | 'braintreeClientInstance' | 'threeDSecureInstance'
> & {
  hide: boolean
}

export const HostedFields = ({
  submitOrder,
  onLoginClick,
  hide,
  braintreeClientInstance,
  threeDSecureInstance,
}: CheckoutPaymentDetailsProps) => {
  const formik = useAddressDetailsForm()
  const deliveryAddress = useSelector(getDeliveryAddress)
  const email = useSelector(getEmail)

  const {
    formValidationError,
    isFormValid,
    handleCardDetailsValidation,
    handleSubmit,
    isFormSubmitting,
    setHostedFieldsFrameLoaded,
  } = useHostedFields({
    braintreeClientInstance,
    submitOrder,
    threeDSecureInstance,
  })

  const isSubmitting = useRef(false)

  const submit = async () => {
    if (!isFormValid || !formik.values.cardHolderName) {
      handleCardDetailsValidation()
      formik.setFieldTouched('cardHolderName')
    }

    const errors = await formik.validateForm()
    const isFormikValid = Object.keys(errors).length === 0
    if (!isFormikValid) {
      formik.setTouched(setNestedObjectValues(errors, true), false)
    }
    if (!isFormValid || !isFormikValid) return

    await formik.submitForm()
    const submitting = handleSubmit({ deliveryAddress, values: formik.values })

    datadogRum.addAction('signup_checkout_click_place_order', {
      hasEmail: Boolean(email),
      hasDeliveryAddress: Boolean(deliveryAddress.get('postcode')),
    })

    await submitting
  }

  const processSubmit = async () => {
    if (isSubmitting.current) return
    isSubmitting.current = true

    try {
      await submit()
    } catch (error) {
      datadogLogs.logger.error('Checkout card payment submit failed', {}, error as Error)
    } finally {
      isSubmitting.current = false
    }
  }

  return (
    <Box display={hide ? Display.None : Display.Block}>
      <FormikProvider value={formik}>
        <Form>
          <CheckoutFrame
            setHostedFieldsFrameLoaded={setHostedFieldsFrameLoaded}
            formValidationError={formValidationError}
          />
          <CheckoutBillingAddress />
          <ErrorMessage onLoginClick={onLoginClick} />
          <SubmitButton onClick={processSubmit} isDisabled={isFormSubmitting} />
        </Form>
      </FormikProvider>
    </Box>
  )
}
